import axios from 'axios';
import appConfig from 'configs/app.config';
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from 'constants/api.constant';
import { PERSIST_STORE_NAME } from 'constants/app.constant';
import deepParseJson from 'utils/deepParseJson';
import store from '../store';
import { onSignOutSuccess } from '../store/auth/sessionSlice';

const unauthorizedCode = [401];

const BaseService = axios.create({
    timeout: 60000,
    baseURL: appConfig.apiPrefix,
});

// ---------------------------------------------------------------------------
// MOCK USERS FROM .env (Development Only)
// ---------------------------------------------------------------------------
const useMock = process.env.REACT_APP_USE_MOCK === 'true';

let MOCK_USERS = [];

if (useMock) {
    try {
        const mockUsersStr = process.env.REACT_APP_MOCK_USERS;
        if (mockUsersStr) {
            MOCK_USERS = JSON.parse(mockUsersStr);
        }
    } catch (error) {
        console.error('❌ Failed to parse REACT_APP_MOCK_USERS from .env', error);
    }
}

// Mock adapter helpers
const mockAdapter = (mockData) => () =>
    Promise.resolve({
        data: mockData,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
    });

const mockErrorAdapter = (message, status) => () =>
    Promise.reject({
        response: { data: { message }, status },
        isAxiosError: true,
    });

// Request Interceptor
BaseService.interceptors.request.use(
    (config) => {
        const url = config.url || '';

        // ====================== MOCK MODE ======================
        if (useMock && MOCK_USERS.length > 0) {
            // --- sign-in ---
            if (url.includes('/auth/sign-in')) {
                const { userName, email, password } = config.data || {};
                const match = MOCK_USERS.find(
                    (u) =>
                        (u.userName === userName ||
                            u.email === userName ||
                            u.email === email) &&
                        u.password === password
                );

                if (!match) {
                    config.adapter = mockErrorAdapter('Invalid username or password', 401);
                } else {
                    config.adapter = mockAdapter({ token: match.token, user: match.user });
                }
                return config;
            }

            // --- sign-up ---
            if (url.includes('/auth/sign-up')) {
                const { userName, email } = config.data || {};
                const exists = MOCK_USERS.find(
                    (u) => u.userName === userName || u.email === email
                );
                if (exists) {
                    config.adapter = mockErrorAdapter('Username or email already exists', 409);
                } else {
                    const newUser = {
                        userId: String(Date.now()),
                        userName: userName || email,
                        name: userName || email,
                        authority: ['user'],
                        avatar: '',
                        email: email || '',
                        role: 'Care Worker',
                        phone: '',
                    };
                    config.adapter = mockAdapter({
                        token: `mock-token-${Date.now()}`,
                        user: newUser,
                    });
                }
                return config;
            }

            // --- sign-out ---
            if (url.includes('/auth/sign-out')) {
                config.adapter = mockAdapter({ message: 'Signed out' });
                return config;
            }

            // --- forgot-password ---
            if (url.includes('/auth/forgot-password')) {
                config.adapter = mockAdapter({ message: 'Reset link sent if account exists' });
                return config;
            }

            // --- reset-password ---
            if (url.includes('/auth/reset-password')) {
                config.adapter = mockAdapter({ message: 'Password updated successfully' });
                return config;
            }
        }
        // ====================== END MOCK MODE ======================

        // Real backend mode - attach token
        const rawPersistData = localStorage.getItem(PERSIST_STORE_NAME);
        const persistData = deepParseJson(rawPersistData);
        const accessToken = persistData?.auth?.session?.token;

        if (accessToken) {
            config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
BaseService.interceptors.response.use(
    (response) => response,
    (error) => {
        const { response } = error;
        if (response && unauthorizedCode.includes(response.status)) {
            store.dispatch(onSignOutSuccess());
        }
        return Promise.reject(error);
    }
);

export default BaseService;